import { AsyncSelect } from './AsyncSelect';
import { SyncSelect } from './SyncSelect';
import { type AsyncSelectProps, type SelectProps } from './types';

/**
 * Select — a customizable dropdown for selecting one or multiple options.
 *
 * Two modes via a discriminated union on `loadOptions`:
 * - **Sync** (default): pass `options` directly → SyncSelect
 * - **Async**: pass `loadOptions` for remote fetching → AsyncSelect
 *
 * Both internal components share the same ActionList/TriggerInput rendering
 * pattern. State management is extracted into hooks (useSyncOptions /
 * useAsyncOptions) so the components stay focused on rendering.
 */
export const Select = <
  isMulti extends boolean = false,
  isNullable extends boolean = false,
  T = undefined,
>(
  props: SelectProps<isMulti, isNullable, T>,
) => {
  if ('loadOptions' in props && props.loadOptions) {
    return <AsyncSelect {...(props as AsyncSelectProps<isNullable, T>)} />;
  }

  return <SyncSelect {...props} />;
};

export { ActionListCheckbox as SelectCheckbox } from '@latitude/action-list';

export {
  type ActionListFullValue as SelectFullValue,
  type ActionListHeaderItem as SelectOptionHeaderItem,
  type ActionListItem as SelectOptionItem,
  type ActionListOption as SelectOption,
} from '@latitude/action-list';

export {
  type AddItemLabel,
  type SelectProps,
  // Internal prop variants — used by @latitude/form for union spread narrowing.
  // External consumers should use SelectProps (the discriminated union).
  type SyncSelectProps,
  type AsyncSelectProps,
  type AsyncLoadOptions,
  type AsyncSelectLoadOptionsArgs,
  type AsyncSelectLoadOptionsReturn,
  type AsyncSelectMode,
  type MultiSelectOverflowBehavior,
} from './types';
